import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Code, BarChart3, Workflow,
  CheckCircle, Users, Award, TrendingUp, Activity,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import HeroShowcase from '../components/HeroShowcase';
import FolderReveal from '../components/FolderReveal';
import { services, stats, pipeline } from '../mock';

const serviceIcons = { Code, BarChart3, Workflow };
const statIcons = { CheckCircle, Users, Award, TrendingUp };

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.6 } }),
};

export default function Home() {
  return (
    <>
      {/* ===================== HERO — cycling showcase ===================== */}
      <HeroShowcase />

      {/* ===================== STATS ===================== */}
      <section className="relative border-y border-line bg-surface">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-line">
            {stats.map((s, i) => {
              const Icon = statIcons[s.icon] || Activity;
              return (
                <motion.div
                  key={s.label}
                  variants={fadeUp}
                  custom={i}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                  className="px-6 py-10 flex items-center gap-4"
                >
                  <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-purple-600/20 to-pink-600/20 border border-line flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <div className="font-display text-3xl font-bold text-strong">{s.value}</div>
                    <div className="font-mono-ui text-[11px] text-faint tracking-wider">{s.label}</div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== SERVICES PREVIEW ===================== */}
      <section className="relative py-24 bg-surface overflow-hidden">
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bloom rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="eyebrow">01</span>
                <span className="eyebrow">What we do</span>
              </div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-strong">
                Three ways to <span className="ink-grad">move faster.</span>
              </h2>
            </div>
            <Link to="/services" className="font-mono-ui text-sm text-purple-500 hover:text-pink-500 transition-colors inline-flex items-center gap-1.5 group shrink-0">
              all services
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {services.map((s, i) => {
              const Icon = serviceIcons[s.icon] || Code;
              return (
                <motion.div
                  key={s.id}
                  variants={fadeUp}
                  custom={i}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                >
                  <Link
                    to="/services"
                    className="group block h-full rounded-2xl border border-line bg-raise hover:bg-raise2 hover:border-purple-500/40 p-6 transition-all duration-300 shadow-sm hover:shadow-md"
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center mb-5 shadow-lg shadow-purple-500/20 group-hover:scale-110 transition-transform">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-display text-lg font-semibold text-strong mb-2">{s.title}</h3>
                    <p className="text-sm text-soft leading-relaxed">{s.description}</p>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== FOLDER — slides in whilst scrolling ===================== */}
      <FolderReveal />

      {/* ===================== PIPELINE (numbered, real sequence) ===================== */}
      <section className="relative py-24 border-t border-line bg-surface overflow-hidden">
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="eyebrow">03</span>
            <span className="eyebrow">How it works</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-strong max-w-2xl mb-14">
            From raw data to <span className="ink-grad">real growth.</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-line rounded-2xl overflow-hidden border border-line">
            {pipeline.map((p, i) => (
              <motion.div
                key={p.step}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="bg-raise p-7 relative"
              >
                <div className="font-display text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-purple-500/50 to-pink-500/50 mb-4">
                  {p.step}
                </div>
                <h3 className="font-display text-xl font-semibold text-strong mb-2">{p.label}</h3>
                <p className="text-sm text-soft leading-relaxed">{p.note}</p>
                {i < pipeline.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute top-7 -right-3 w-5 h-5 text-purple-500/60 z-10" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== CTA BAND ===================== */}
      <section className="relative py-24 bg-surface overflow-hidden border-t border-line">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bloom rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="liquid-glass w-24 h-24 rounded-3xl mx-auto mb-8 flex items-center justify-center">
            <img
              src="/images/tech-orb.png"
              alt="Data and automation illustration"
              className="w-16 h-16 object-contain"
              loading="lazy"
            />
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-strong max-w-2xl mx-auto mb-6">
            Ready to get your data <span className="ink-grad">in sync?</span>
          </h2>
          <p className="text-lg text-soft max-w-xl mx-auto mb-9">
            Tell us what you're building. We'll show you what smarter, faster decisions look like.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-9 py-6 text-base rounded-xl transition-all duration-300 hover:scale-105 shadow-2xl shadow-purple-500/30 group"
            >
              Start a project
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </>
  );
}
